import { useEffect, useState, useCallback } from "react";
import api, { WEBHOOK_URL } from "../lib/api";
import { toast } from "sonner";
import {
  Copy, RefreshCw, Save, Zap, Send, PlugZap, CheckCircle2, XCircle,
  CircleDashed, Loader2, Terminal, ShieldCheck, Facebook, ExternalLink,
} from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#E9EDEF] focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] text-[#111B21] rounded-lg px-3 py-2 text-sm font-mono placeholder:text-[#8696A0] focus:outline-none transition-colors";

const CHECK_LABELS = {
  meta_reachability: "Meta API Reachability",
  access_token: "Credential / Read Validity",
  waba: "WhatsApp Business Account",
  phone_number_id: "Phone Number ID",
  phone_number: "Phone Number",
  messaging: "Messaging Eligibility",
  template_management: "Template Management Access",
  permissions: "WABA Membership",
  webhook: "Webhook",
};

function passState(status) {
  const good = ["PASS", "VALID", "FOUND", "AVAILABLE", "CONFIGURED", "CONNECTED"];
  const bad = ["FAIL", "INVALID", "NOT_FOUND", "ERROR", "MISSING", "NOT_CONFIGURED", "BLOCKED", "RESTRICTED"];
  const warn = ["UNKNOWN", "VIEW_ONLY", "PENDING"];
  if (good.includes(status)) return "good";
  if (bad.includes(status)) return "bad";
  if (warn.includes(status)) return "warn";
  return "pending";
}

function copy(text) {
  navigator.clipboard.writeText(text);
  toast.success("Copied to clipboard");
}

const WIZARD_STEPS = [
  "API credentials", "WhatsApp Business Account", "Phone Number ID", "Access token",
  "Webhook", "Incoming message", "AI processing", "Outgoing message", "Inbox synchronization",
];

export default function WhatsAppSettings() {
  const [cfg, setCfg] = useState({
    app_id: "", app_secret: "", business_account_id: "", phone_number_id: "",
    business_phone: "", access_token: "", graph_api_version: "v22.0",
  });
  const [meta, setMeta] = useState({ configured: false });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [diag, setDiag] = useState(null);

  const [testTo, setTestTo] = useState("");
  const [testMsg, setTestMsg] = useState("Hello from MuviArts.Online 👋");
  const [sending, setSending] = useState(false);

  const [wizard, setWizard] = useState(null); // array of {label, state}
  const [wizardRunning, setWizardRunning] = useState(false);
  const [embed, setEmbed] = useState({ enabled: false });
  const [connecting, setConnecting] = useState(false);

  useEffect(() => { api.get("/whatsapp/embedded-config").then((r) => setEmbed(r.data)).catch(() => {}); }, []);

  const loadFbSdk = (appId) => new Promise((resolve) => {
    if (window.FB) { resolve(); return; }
    window.fbAsyncInit = function () {
      window.FB.init({ appId, cookie: true, xfbml: false, version: "v22.0" });
      resolve();
    };
    const s = document.createElement("script");
    s.src = "https://connect.facebook.net/en_US/sdk.js";
    s.async = true; s.defer = true; document.body.appendChild(s);
  });

  const connectOneClick = async () => {
    if (!embed.enabled) { toast.error("1-click connect is not configured by the admin yet."); return; }
    setConnecting(true);
    let sessionInfo = {};
    const listener = (event) => {
      if (!String(event.origin).endsWith("facebook.com")) return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === "WA_EMBEDDED_SIGNUP") sessionInfo = data.data || {};
      } catch (e) {}
    };
    window.addEventListener("message", listener);
    try {
      await loadFbSdk(embed.app_id);
      window.FB.login(async (resp) => {
        const code = resp?.authResponse?.code;
        window.removeEventListener("message", listener);
        if (!code) { setConnecting(false); toast.error("Facebook sign-in cancelled"); return; }
        try {
          await api.post("/whatsapp/embedded-signup", {
            code, waba_id: sessionInfo.waba_id, phone_number_id: sessionInfo.phone_number_id,
          });
          toast.success("WhatsApp connected! Run Test Connection to verify.");
          load();
        } catch (e) { toast.error(e.response?.data?.detail || "Connect failed"); }
        finally { setConnecting(false); }
      }, { config_id: embed.config_id, response_type: "code", override_default_response_type: true, extras: { setup: {} } });
    } catch (e) {
      window.removeEventListener("message", listener);
      setConnecting(false); toast.error("Could not start Facebook signup");
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get("/whatsapp/integration");
      setMeta(r.data);
      if (r.data.configured) {
        setCfg((c) => ({
          ...c,
          app_id: r.data.app_id || "",
          business_account_id: r.data.business_account_id || "",
          phone_number_id: r.data.phone_number_id || "",
          business_phone: r.data.business_phone || "",
          graph_api_version: r.data.graph_api_version || "v22.0",
          app_secret: "", access_token: "",
        }));
      }
    } catch (e) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      if (meta.configured && !payload.app_secret) delete payload.app_secret;
      if (meta.configured && !payload.access_token) delete payload.access_token;
      const r = await api.post("/whatsapp/integration", payload);
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true); setDiag(null);
    try {
      const r = await api.post("/whatsapp/test");
      setDiag(r.data);
      if (r.data.overall === "CONNECTED") toast.success("Messaging eligible");
      else if (r.data.overall === "BLOCKED") toast.error("Account BLOCKED by Meta — see diagnostics");
      else if (r.data.overall === "RESTRICTED") toast.warning ? toast.warning("Credentials read OK, but messaging not confirmed") : toast("Restricted — see diagnostics");
      else toast.error("Connection error — see diagnostics");
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  const copyVerifyToken = async () => {
    try { const r = await api.get("/whatsapp/verify-token"); copy(r.data.verify_token); }
    catch (e) { toast.error("Save configuration first"); }
  };
  const regenToken = async () => {
    try { const r = await api.post("/whatsapp/verify-token/regenerate"); copy(r.data.verify_token); toast.success("New verify token generated & copied"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter recipient number"); return; }
    setSending(true);
    try {
      const r = await api.post("/whatsapp/send", { to: testTo, message: testMsg });
      if (r.data.ok) toast.success(`✓ Message accepted by Meta (${r.data.provider_message_id?.slice(0, 12)}…)`);
      else toast.error(`✗ ${r.data.error} ${r.data.error_code ? "(" + r.data.error_code + ")" : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const runWizard = async () => {
    setWizardRunning(true);
    const steps = WIZARD_STEPS.map((l) => ({ label: l, state: "pending" }));
    setWizard([...steps]);
    // Steps 1-5: run diagnostics
    try {
      const r = await api.post("/whatsapp/test");
      const c = r.data.checks;
      steps[0].state = passState(c.access_token.status) === "good" ? "passed" : "failed";
      steps[1].state = passState(c.waba.status) === "good" ? "passed" : "failed";
      steps[2].state = passState(c.phone_number_id.status) === "good" ? "passed" : "failed";
      steps[3].state = passState(c.access_token.status) === "good" ? "passed" : "failed";
      steps[4].state = passState(c.webhook.status) === "good" ? "passed" : "failed";
      setWizard([...steps]);
    } catch (e) {
      steps.forEach((s, i) => { if (i < 5) s.state = "failed"; });
      setWizard([...steps]);
      setWizardRunning(false);
      toast.error("Credential checks failed. Fix configuration first.");
      return;
    }
    // Steps 6-9: live detection
    steps[5].state = "testing"; setWizard([...steps]);
    toast.info("Now send a WhatsApp message to your MuviArts number from another phone.");
    const start = Date.now();
    const statusR = await api.get("/whatsapp/status");
    const baselineIn = statusR.data.last_incoming_at;
    const baselineOut = statusR.data.last_outgoing_at;

    let elapsed = 0;
    const poll = setInterval(async () => {
      elapsed += 5;
      try {
        const s = await api.get("/whatsapp/status");
        if (s.data.last_incoming_at && s.data.last_incoming_at !== baselineIn) {
          steps[5].state = "passed"; steps[6].state = "testing"; setWizard([...steps]);
        }
        if (s.data.last_outgoing_at && s.data.last_outgoing_at !== baselineOut) {
          steps[6].state = "passed"; steps[7].state = "passed"; steps[8].state = "passed";
          setWizard([...steps]);
          clearInterval(poll); setWizardRunning(false);
          toast.success("End-to-end WhatsApp AI test passed!");
        }
      } catch (e) {}
      if (elapsed >= 120) {
        clearInterval(poll); setWizardRunning(false);
        if (steps[5].state !== "passed") { steps[5].state = "failed"; setWizard([...steps]); toast.error("No incoming message detected in 2 min."); }
      }
    }, 5000);
  };

  return (
    <div className="space-y-6" data-testid="whatsapp-settings">
      <div>
        <h2 className="font-heading text-2xl font-medium tracking-tight text-[#111B21]">Connect MuviArts WhatsApp</h2>
        <p className="text-sm text-[#8696A0] mt-1">Connect your official MuviArts WhatsApp Business number using the Meta WhatsApp Cloud API.</p>
      </div>

      {/* 1-click connect */}
      <div className="border border-[#E9EDEF] bg-white rounded-lg border-l-2 border-l-[#25D366] p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="oneclick-connect">
        <div className="h-10 w-10 rounded-lg bg-[#E7FCE8] border border-[#25D366]/30 flex items-center justify-center flex-shrink-0">
          <Facebook className="h-5 w-5 text-[#075E54]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-[#111B21]">Connect in 1 click</p>
          <p className="text-xs text-[#8696A0]">
            {embed.enabled
              ? "Use Meta Embedded Signup — sign in with Facebook and pick your WhatsApp number. No manual credentials needed."
              : "1-click connect activates once the admin adds the Meta App ID + Config ID (Admin → 1-Click / Platform). For now, use the manual form below."}
          </p>
        </div>
        <button data-testid="oneclick-button" onClick={connectOneClick} disabled={!embed.enabled || connecting}
          className="flex items-center justify-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0 w-full sm:w-auto">
          {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Facebook className="h-4 w-4" />}
          Connect with Facebook
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-[#F0F2F5]" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#8696A0]">or configure manually</span>
        <div className="flex-1 h-px bg-[#F0F2F5]" />
      </div>

      {/* Credentials */}
      <div className="border border-[#E9EDEF] bg-white rounded-lg">
        <div className="px-5 py-4 border-b border-[#E9EDEF] flex flex-wrap items-center gap-2">
          <PlugZap className="h-4 w-4 text-[#075E54]" />
          <h3 className="font-heading text-sm font-medium text-[#111B21]">Meta Credentials</h3>
          <span className="ml-auto font-mono text-[10px] text-[#8696A0]">provider: meta_cloud_api</span>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { k: "app_id", label: "Meta App ID", ph: "1234567890", type: "text" },
            { k: "app_secret", label: "Meta App Secret", ph: meta.app_secret_masked || "••••••••", type: "password" },
            { k: "business_account_id", label: "WhatsApp Business Account ID", ph: "WABA ID", type: "text" },
            { k: "phone_number_id", label: "Phone Number ID", ph: "Phone Number ID", type: "text" },
            { k: "business_phone", label: "Business Phone Number", ph: "+91XXXXXXXXXX", type: "text" },
            { k: "access_token", label: "Access Token (long-lived)", ph: meta.access_token_masked || "••••••••", type: "password" },
          ].map((f) => (
            <div key={f.k}>
              <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">{f.label}</label>
              <input data-testid={`wa-${f.k}`} type={f.type} value={cfg[f.k]} placeholder={f.ph}
                onChange={(e) => setCfg({ ...cfg, [f.k]: e.target.value })} className={inputCls + " mt-1"} />
            </div>
          ))}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Graph API Version</label>
            <input data-testid="wa-graph_api_version" value={cfg.graph_api_version}
              onChange={(e) => setCfg({ ...cfg, graph_api_version: e.target.value })} className={inputCls + " mt-1"} />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#E9EDEF] flex items-center gap-3">
          <button data-testid="wa-save-config" onClick={save} disabled={saving}
            className="flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-white rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Configuration"}
          </button>
          {meta.configured && <span className="font-mono text-[10px] text-[#8696A0]">status: {meta.status}</span>}
        </div>
      </div>

      {/* Webhook */}
      <div className="border border-[#E9EDEF] bg-white rounded-lg border-l-2 border-l-[#25D366]">
        <div className="px-5 py-4 border-b border-[#E9EDEF] flex flex-wrap items-center gap-2">
          <Terminal className="h-4 w-4 text-[#075E54]" />
          <h3 className="font-heading text-sm font-medium text-[#111B21]">Webhook Configuration</h3>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Webhook URL (Callback URL)</label>
            <div className="flex gap-2 mt-1">
              <input readOnly value={WEBHOOK_URL} data-testid="webhook-url" className={inputCls + " text-[#075E54]"} />
              <button data-testid="copy-webhook-url" onClick={() => copy(WEBHOOK_URL)}
                className="flex items-center gap-1 border border-[#CBD5E1] text-[#374151] hover:bg-[#E9EDEF] rounded-lg px-3 text-xs transition-colors">
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
            </div>
            <p className="text-xs text-[#8696A0] mt-1">Paste this into Meta → WhatsApp → Configuration → Callback URL.</p>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">Webhook Verify Token</label>
            <div className="flex gap-2 mt-1">
              <input readOnly value={meta.has_verify_token ? "••••••••••••••••••••" : "— save config to generate —"} className={inputCls} />
              <button data-testid="copy-verify-token" onClick={copyVerifyToken} disabled={!meta.has_verify_token}
                className="flex items-center gap-1 border border-[#CBD5E1] text-[#374151] hover:bg-[#E9EDEF] rounded-lg px-3 text-xs transition-colors disabled:opacity-40">
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
              <button data-testid="regen-verify-token" onClick={regenToken}
                className="flex items-center gap-1 border border-[#CBD5E1] text-[#374151] hover:bg-[#E9EDEF] rounded-lg px-3 text-xs transition-colors">
                <RefreshCw className="h-3.5 w-3.5" /> Regenerate
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Test connection + diagnostics */}
      <div className="border border-[#E9EDEF] bg-white rounded-lg">
        <div className="px-5 py-4 border-b border-[#E9EDEF] flex flex-wrap items-center gap-2">
          <Zap className="h-4 w-4 text-amber-500" />
          <h3 className="font-heading text-sm font-medium text-[#111B21]">Connection Diagnostics</h3>
          <button data-testid="wa-test-connection" onClick={testConnection} disabled={testing}
            className="ml-auto flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Test Connection
          </button>
        </div>
        <div className="p-5">
          {!diag ? (
            <p className="text-sm text-[#8696A0]">Run a real Meta Graph API test to validate your credentials.</p>
          ) : (
            <div data-testid="diagnostics-panel" className="space-y-2">
              {(() => {
                const ov = diag.overall;
                const cls = ov === "CONNECTED" ? "bg-green-500/10 text-green-600 border-green-500/20"
                  : ov === "RESTRICTED" ? "bg-amber-500/10 text-amber-600 border-amber-500/20"
                  : "bg-red-500/10 text-red-600 border-red-500/20";
                const label = ov === "BLOCKED" ? "BLOCKED — account locked/restricted by Meta"
                  : ov === "RESTRICTED" ? "RESTRICTED — credentials read OK, but messaging not confirmed"
                  : ov === "CONNECTED" ? "CONNECTED — messaging eligible" : ov;
                return (
                  <div className={`flex items-center gap-2 mb-1 px-3 py-2 rounded-lg border font-mono text-xs ${cls}`}>
                    {ov === "CONNECTED" ? <CheckCircle2 className="h-4 w-4" /> : ov === "RESTRICTED" ? <CircleDashed className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    OVERALL: {label}
                  </div>
                );
              })()}
              {diag.lock_reason && (
                <p className="text-xs text-red-600 bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2 break-words" data-testid="lock-reason">
                  Lock reason (Meta): {diag.lock_reason}
                </p>
              )}
              {diag.meta_review_link && (
                <a href={diag.meta_review_link} target="_blank" rel="noreferrer" data-testid="meta-review-link"
                  className="inline-flex items-center gap-1.5 text-xs text-[#075E54] hover:underline break-all">
                  <ExternalLink className="h-3.5 w-3.5 shrink-0" /> Open Meta account review / support for this account
                </a>
              )}
              <p className="text-[11px] text-[#8696A0] italic">Note: a successful read does not mean messages will deliver; delivery is still subject to Meta's 24-hour window and template rules.</p>
              {Object.entries(diag.checks).map(([k, v]) => {
                const st = passState(v.status);
                return (
                  <div key={k} className="flex items-start gap-3 py-2 border-b border-[#E9EDEF] last:border-0">
                    {st === "good" ? <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" /> :
                      st === "bad" ? <XCircle className="h-4 w-4 text-red-600 mt-0.5" /> :
                      st === "warn" ? <CircleDashed className="h-4 w-4 text-amber-500 mt-0.5" /> :
                      <CircleDashed className="h-4 w-4 text-[#8696A0] mt-0.5" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#111B21]">{CHECK_LABELS[k] || k}</span>
                        <span className={`font-mono text-[10px] ${st === "good" ? "text-green-600" : st === "bad" ? "text-red-600" : st === "warn" ? "text-amber-500" : "text-[#8696A0]"}`}>{v.status}</span>
                      </div>
                      <p className="text-xs text-[#8696A0] mt-0.5 break-words">{v.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Send test message */}
      <div className="border border-[#E9EDEF] bg-white rounded-lg">
        <div className="px-5 py-4 border-b border-[#E9EDEF] flex flex-wrap items-center gap-2">
          <Send className="h-4 w-4 text-[#075E54]" />
          <h3 className="font-heading text-sm font-medium text-[#111B21]">Send Test Message</h3>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input data-testid="test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Recipient +91XXXXXXXXXX" className={inputCls} />
            <input data-testid="test-message" value={testMsg} onChange={(e) => setTestMsg(e.target.value)} placeholder="Message" className={inputCls + " md:col-span-2 font-sans"} />
          </div>
          <button data-testid="send-test-message" onClick={sendTest} disabled={sending}
            className="flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-white rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Test Message
          </button>
          <p className="text-xs text-[#8696A0]">Note: outside a 24-hour customer service window, Meta requires a pre-approved template (error 131047).</p>
        </div>
      </div>

      {/* End-to-end wizard */}
      <div className="border border-[#E9EDEF] bg-white rounded-lg border-l-2 border-l-purple-500/50">
        <div className="px-5 py-4 border-b border-[#E9EDEF] flex flex-wrap items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[#075E54]" />
          <h3 className="font-heading text-sm font-medium text-[#111B21]">Test WhatsApp AI (End-to-End)</h3>
          <button data-testid="run-wizard" onClick={runWizard} disabled={wizardRunning}
            className="ml-auto flex items-center gap-2 bg-[#E7FCE8] text-[#128C7E] border border-[#25D366]/30 hover:bg-[#DCF8C6] rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
            {wizardRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Run Test
          </button>
        </div>
        <div className="p-5">
          <p className="text-xs text-[#8696A0] mb-4">Runs credential checks, then asks you to send a live WhatsApp message from another phone (e.g. "Hi, what services do you provide?") and detects the full flow.</p>
          <div className="space-y-1" data-testid="wizard-steps">
            {(wizard || WIZARD_STEPS.map((l) => ({ label: l, state: "pending" }))).map((s, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-[#E9EDEF] last:border-0">
                {s.state === "passed" ? <CheckCircle2 className="h-4 w-4 text-green-600" /> :
                  s.state === "failed" ? <XCircle className="h-4 w-4 text-red-600" /> :
                  s.state === "testing" ? <Loader2 className="h-4 w-4 text-[#075E54] animate-spin" /> :
                  <CircleDashed className="h-4 w-4 text-[#8696A0]" />}
                <span className="text-sm text-[#374151]">{i + 1}. {s.label}</span>
                <span className="ml-auto font-mono text-[10px] text-[#8696A0] uppercase">{s.state}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
